import React from 'react'
import { revalidateField } from './importValidator'
import { getErrorFieldLabel } from './importUtils'
import styles from './style.module.scss'

// v2.7: 에러 객체에 messageKey가 있으면 그걸 사용. 없으면 legacy reason → key 맵 폴백.
const ERROR_REASON_MAP = {
  requiredEmpty: 'record.import.v2.validation.error.requiredEmpty',
  formatError: 'record.import.v2.validation.error.formatError',
  // 하위 호환 (이전 reason 값)
  urlFormat: 'record.import.v2.validation.error.formatError',
  dateFormat: 'record.import.v2.validation.error.formatError',
}

const getErrorMessage = (e, t) => {
  if (e.messageKey) {
    /* i18next-extract-disable-next-line */
    return t(e.messageKey, e.messageParams || {})
  }
  /* i18next-extract-disable-next-line */
  return t(ERROR_REASON_MAP[e.reason] || e.reason)
}

const CheckIcon = () => (
  <svg width="13" height="10" viewBox="0 0 13 10">
    <path
      d="M1 5l4 4 7-8"
      stroke="currentColor"
      strokeWidth="1.8"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

// v2.5: 검증 화면 amber 경고 배너 아이콘 (Figma: 삼각형 + 느낌표)
const WarningIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M10 2L18 17H2L10 2z" fill="#FBBF24" />
    <path d="M10 8v4M10 14v0.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

const ImportValidationStep = ({ errors, dispatch, t }) => {
  const fixedList = errors.filter((e) => e.action === 'fixed')
  const exclList = errors.filter((e) => e.action === 'exclude')
  // 배지(failWithCount)는 초기 총 오류 누적 맥락 표시용 → errors.length 기준 의도적으로 유지.
  // 배너(alertBanner)는 아직 완료되지 않은 오류가 있을 때만 노출 → pendingCount 기준.
  // editing은 사용자 편집 중(미완료 전이 상태)이므로 pending에 포함.
  const hasError = errors.length > 0
  const pendingCount = errors.filter((e) => e.action !== 'fixed' && e.action !== 'exclude').length
  const hasPending = pendingCount > 0

  const handleStartEdit = (id) => {
    dispatch({ type: 'SET_ERROR_EDITING', payload: { id } })
  }

  const handleFixChange = (id, fix, uiType) => {
    // 이메일 필드는 입력 시점에 자동 소문자화 (사용자가 대문자로 쳐도 lower 로 정규화)
    const normalized = uiType === 'email' || uiType === 'secure_email' ? fix.toLowerCase() : fix
    dispatch({ type: 'SET_ERROR_FIX', payload: { id, fix: normalized } })
  }

  const handleExclude = (id) => {
    dispatch({ type: 'SET_ERROR_EXCLUDE', payload: { id } })
  }

  const handleCancel = (id) => {
    dispatch({ type: 'CANCEL_ERROR_ACTION', payload: { id } })
  }

  const handleSave = (err) => {
    const value = (err.fix || '').trim()
    // v2.7: 에러 객체 전체를 전달 (uiType/minLength 기반 정확 재검증)
    const result = revalidateField(err, value)
    if (!result.valid) {
      // 재검증 실패 시 **새 reason/messageKey**로 UI 메시지 업데이트 (원래 err의 메시지 말고)
      dispatch({
        type: 'SET_EDIT_ERROR',
        payload: {
          id: err.id,
          message: result.messageKey
            ? /* i18next-extract-disable-next-line */
              t(result.messageKey, result.messageParams || {})
            : getErrorMessage(err, t),
        },
      })
      return
    }
    dispatch({ type: 'COMMIT_ERROR_FIX', payload: { id: err.id } })
  }

  // 상태별 액션 버튼 렌더링
  const renderActions = (e) => {
    // 편집 중: 저장 / 취소
    if (e.action === 'editing') {
      const canSave = (e.fix || '').trim().length > 0
      return (
        <>
          <button
            type="button"
            className={[
              styles.actionBtn,
              styles.actionBtnSave,
              !canSave ? styles.actionBtnDisabled : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => handleSave(e)}
            disabled={!canSave}
          >
            {t('record.import.v2.validation.save')}
          </button>
          <button type="button" className={styles.actionBtn} onClick={() => handleCancel(e.id)}>
            {t('record.import.v2.validation.cancel')}
          </button>
        </>
      )
    }

    // 확정된 상태 (수정/제외): 취소 버튼만
    if (e.action === 'fixed' || e.action === 'exclude') {
      return (
        <button type="button" className={styles.actionBtn} onClick={() => handleCancel(e.id)}>
          {t('record.import.v2.validation.cancel')}
        </button>
      )
    }

    // 대기 상태: 수정 / 제외
    return (
      <>
        <button type="button" className={styles.actionBtn} onClick={() => handleStartEdit(e.id)}>
          {t('record.import.v2.validation.fix')}
        </button>
        <button type="button" className={styles.actionBtn} onClick={() => handleExclude(e.id)}>
          {t('record.import.v2.validation.exclude')}
        </button>
      </>
    )
  }

  return (
    <div className={styles.validationStep}>
      {/* v2.5: 상단 amber 경고 배너 (Figma 기준) — pending 오류가 있을 때만 표시 */}
      {hasPending && (
        <div className={styles.alertBanner} role="alert">
          <WarningIcon />
          <span>{t('record.import.v2.validation.alertMessage')}</span>
        </div>
      )}

      {/* 검증 결과 헤더: "업로드 결과" + "실패 (오류 N건)" */}
      <div className={styles.validationSummary}>
        <div className={styles.validationSummaryLeft}>
          <span className={styles.validationTitle}>
            {t('record.import.v2.validation.resultTitle')}
          </span>
          {hasError && (
            <span className={styles.failBadge}>
              {t('record.import.v2.validation.failWithCount', { count: errors.length })}
            </span>
          )}
        </div>
        {(fixedList.length > 0 || exclList.length > 0) && (
          <div className={styles.validationSummaryMeta}>
            {fixedList.length > 0 && (
              <span className={styles.summaryTextMeta}>
                {t('record.import.v2.validation.fixed', { count: fixedList.length })}
              </span>
            )}
            {exclList.length > 0 && (
              <span className={styles.summaryTextMeta}>
                {t('record.import.v2.validation.excluded', { count: exclList.length })}
              </span>
            )}
          </div>
        )}
      </div>

      {/* 오류 0건: 유효 메시지만 표시 */}
      {!hasError && (
        <div className={styles.allValidMessage}>{t('record.import.v2.validation.allValid')}</div>
      )}

      {/* 오류 목록 테이블 */}
      {hasError && (
        <div className={styles.errorTable}>
          <div className={styles.errorTableHeader}>
            <div className={styles.errorColRow}>{t('record.import.v2.validation.row')}</div>
            <div className={styles.errorColReason}>{t('record.import.v2.validation.reason')}</div>
            <div className={styles.errorColField}>{t('record.import.v2.validation.field')}</div>
            <div className={styles.errorColValue}>
              {t('record.import.v2.validation.currentValue')}
            </div>
            <div className={styles.errorColAction}>{t('record.import.v2.validation.action')}</div>
          </div>

          {errors.map((e, idx) => {
            const isLastRow = idx === errors.length - 1
            const rowClass = [
              styles.errorTableRow,
              e.action === 'exclude' ? styles.errorRowExcluded : '',
              e.action === 'fixed' ? styles.errorRowFixed : '',
              e.action === 'editing' ? styles.errorRowEditing : '',
            ]
              .filter(Boolean)
              .join(' ')

            // 현재 값 셀 렌더링 (중첩 삼항 회피)
            // v2.8: 현재 값 10글자 초과 시 말줄임표. hover 시 title로 전체 값 표시
            const truncate = (v) => (v && v.length > 10 ? `${v.slice(0, 10)}...` : v)
            let valueCell
            if (e.action === 'fixed') {
              valueCell = (
                <span className={styles.errorValueFixed} title={e.fix}>
                  {truncate(e.fix)}
                </span>
              )
            } else if (e.value) {
              valueCell = (
                <span className={styles.errorValue} title={e.value}>
                  {truncate(e.value)}
                </span>
              )
            } else {
              valueCell = (
                <span className={styles.errorEmpty}>{t('record.import.v2.validation.empty')}</span>
              )
            }

            return (
              <div key={e.id}>
                <div
                  className={rowClass}
                  style={{ borderBottom: isLastRow ? 'none' : '1px solid #F1F5F9' }}
                >
                  <div className={styles.errorColRow}>
                    <span className={styles.errorRowNum}>{e.row}</span>
                  </div>
                  <div className={styles.errorColReason}>
                    <span className={styles.textError}>{getErrorMessage(e, t)}</span>
                  </div>
                  <div className={styles.errorColField}>{getErrorFieldLabel(e, t)}</div>
                  <div className={styles.errorColValue}>{valueCell}</div>
                  <div className={styles.errorColAction}>{renderActions(e)}</div>
                </div>

                {/* 편집 모드: 행 아래에 입력창 + 재검증 실패 메시지 */}
                {e.action === 'editing' && (
                  <div className={styles.fixInputRow}>
                    <input
                      className={[styles.fixInput, e.editError ? styles.fixInputError : '']
                        .filter(Boolean)
                        .join(' ')}
                      value={e.fix || ''}
                      onChange={(ev) => handleFixChange(e.id, ev.target.value, e.uiType)}
                      onKeyDown={(ev) => {
                        if (ev.key === 'Enter' && (e.fix || '').trim()) handleSave(e)
                      }}
                      placeholder={t('record.import.v2.validation.fixPlaceholder')}
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus
                    />
                    {e.editError && (
                      <span className={styles.fixInputErrorMessage}>{e.editError}</span>
                    )}
                    {!e.editError && (e.fix || '').trim() && (
                      <span className={styles.fixInputHint}>
                        <CheckIcon />
                        {t('record.import.v2.validation.willValidate')}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ImportValidationStep
